// src/client/inventory/ClientInventory.jsx
import React, { useState } from "react";
import { collection, doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import useCurrentUser from "../../hooks/useCurrentUser";
import useInventory from "./useInventory";

import StatCard from "./StatCard";
import AddMaterialForm from "./AddMaterialForm";
import TransferBlockReserve from "./TransferBlockReserve";
import InventoryTable from "./InventoryTable";
import TopMaterialsChart from "./TopMaterialsChart";
import ClientHistory from "../ClientHistory";
import Modal from "../../components/Modal";
import {
  Package,
  DollarSign,
  Hash,
  Activity,
  Plus,
  ArrowRightLeft,
  Search,
  Download,
  X
} from "lucide-react";

export default function ClientInventory() {
  const { clientId } = useCurrentUser();
  const {
    columns,
    materials,
    newRow,
    editingId,
    editedRow,
    transfer,
    blockQty,
    reserveQty,
    totalStock,
    totalValue,
    top10ByValue,
    setNewRow,
    setEditedRow,
    setTransfer,
    setBlockQty,
    setReserveQty,
    addMaterial,
    deleteRow,
    startEditing,
    saveEdit,
    handleRowChange,
    blockStock,
    reserveStock,
    normalizeKey,
    setEditingId,
    search,
    setSearch,
  } = useInventory();

  const [showTransfer, setShowTransfer] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const handleViewLogs = (materialId) => {
    setSelectedMaterial(materialId);
    setShowLogs(true);
  };

  const handleCloseLogs = () => {
    setShowLogs(false);
    setSelectedMaterial(null);
  };

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900 font-serif">Inventory Management</h1>
          <p className="text-slate-500 mt-1">Track stock levels, value, and material movements.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTransfer(true)}
            className="px-4 py-2 bg-white border border-slate-300 text-navy-900 rounded-lg hover:bg-slate-50 font-medium flex items-center gap-2 transition-colors"
          >
            <ArrowRightLeft size={18} /> Transfer
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 font-medium flex items-center gap-2 transition-colors shadow-lg shadow-navy-900/20"
          >
            <Plus size={18} /> Add Material
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Stock"
          value={`${totalStock.toLocaleString()}`}
          sub="Cartons in warehouse"
          icon={Package}
          color="blue"
        />
        <StatCard
          label="Total Value"
          value={`$${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub="Combined inventory value"
          icon={DollarSign}
          color="emerald"
        />
        <StatCard
          label="SKU Count"
          value={materials.length}
          sub="Unique materials tracked"
          icon={Hash}
          color="violet"
        />
        <StatCard
          label="Avg Unit Price"
          value={`$${(totalStock > 0 ? totalValue / totalStock : 0).toFixed(2)}`}
          sub="Per carton average"
          icon={Activity}
          color="amber"
        />
      </div>

      {/* Toolbar & Filter */}
      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm mb-6 flex flex-col md:flex-row items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all placeholder:text-slate-400 text-sm"
            placeholder="Search by material number, description, or location..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-slate-400 font-medium hidden md:block">
            {materials.length} material{materials.length !== 1 ? "s" : ""}
          </span>
          <button className="p-2.5 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200" title="Export">
            <Download size={16} />
          </button>
        </div>
      </div>

      {showTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <div>
                <h3 className="font-bold text-navy-900 flex items-center gap-2"><ArrowRightLeft size={18} className="text-gold-500" /> Transfer / Block Inventory</h3>
                <p className="text-xs text-slate-400 mt-0.5">Move stock between locations or set blocks</p>
              </div>
              <button onClick={() => setShowTransfer(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"><X size={18} /></button>
            </div>
            <div className="p-6">
              <TransferBlockReserve
                materials={materials}
                transfer={transfer}
                setTransfer={setTransfer}
                blockQty={blockQty}
                setBlockQty={setBlockQty}
                reserveQty={reserveQty}
                setReserveQty={setReserveQty}
                onTransfer={async () => {
                  const mat = materials.find((m) => m.id === transfer.materialId);
                  if (!mat) return;
                  const qty = parseFloat(transfer.qty);
                  if (qty > parseFloat(mat.values.qty)) return;
                  await updateDoc(doc(db, "inventories", mat.id), {
                    ...mat.values,
                    qty: parseFloat(mat.values.qty) - qty,
                  });
                  const ref = doc(collection(db, "inventories"));
                  await setDoc(ref, { ...mat.values, qty, location: transfer.to });
                  setTransfer({ materialId: "", to: "", qty: 0 });
                  setShowTransfer(false);
                }}
                onBlock={blockStock}
                onReserve={reserveStock}
                onClose={() => setShowTransfer(false)}
              />
            </div>
          </div>
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)}>
        <AddMaterialForm
          columns={columns}
          newRow={newRow}
          onChange={(e) => handleRowChange(e, setNewRow)}
          onAdd={() => { addMaterial(); setShowAdd(false); }}
        />
      </Modal>

      {/* Inventory Table */}
      <InventoryTable
        materials={materials}
        columns={columns}
        editingId={editingId}
        editedRow={editedRow}
        normalizeKey={normalizeKey}
        onEdit={setEditedRow}
        onDelete={deleteRow}
        onChange={handleRowChange}
        onSave={saveEdit}
        onCancel={() => {
          setEditingId(null);
          setEditedRow({});
        }}
        onStartEdit={startEditing}
        onViewLogs={handleViewLogs}
      />

      {/* Top 10 Chart */}
      <div className="mt-8">
        <TopMaterialsChart data={top10ByValue} />
      </div>

      {/* Logs Popup */}
      {showLogs && (
        <ClientHistory
          clientId={clientId}
          materialId={selectedMaterial}
          onClose={handleCloseLogs}
        />
      )}
    </div>
  );
}
